import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import { ArrowLeft, Check, Music, Mic, Pen, Star, Calendar, Clock, Mail, User, CreditCard, Sparkles, Heart, Phone, ChevronDown, Users, Volume2, Award, MapPin, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [openPackageId, setOpenPackageId] = useState('premium'); // Premium open by default to highlight the main seller
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    date: '',
    time: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Scroll to top when page loads
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const BOOKING_EMAIL = 'dionlovellebooking@gmail.com';
  const BOOKING_PHONE = '209-424-9048';

  const livePackages = [
    {
      id: 'essential',
      name: 'Essential Package',
      priceRange: '$350 – $500',
      tagline: 'Best for: small gatherings, casual events',
      accent: 'from-sky-400 to-indigo-400',
      icon: <Music size={28} />,
      features: [
        'Up to 2 hours of live performance',
        'Professional vocal',
        'Basic sound setup',
        '3 custom song requests',
      ],
    },
    {
      id: 'premium',
      name: 'Premium Package',
      priceRange: '$600 – $800',
      tagline: 'Best for: weddings, romantic events, upscale dinners',
      badge: 'MAIN SELLER',
      accent: 'from-yellow-400 to-orange-500',
      icon: <Sparkles size={28} />,
      features: [
        'Up to 3–4 hours of live music',
        'Curated setlist (romantic / vibe-based)',
        'Professional sound setup',
        '5 custom song requests',
        'Break music included (playlist between sets)',
      ],
    },
    {
      id: 'intimate',
      name: 'Intimate Experience',
      priceRange: '$900 – $1,200+',
      tagline: 'Best for: weddings, luxury hotels, high-end clients',
      accent: 'from-pink-400 to-purple-500',
      icon: <Heart size={28} />,
      features: [
        'Up to 4 hours performance',
        'Fully customized setlist',
        'Special moment songs (walk down aisle, first dance, etc.)',
        'Premium sound experience',
        'Priority booking + consultation call',
      ],
    },
  ];

  const handlePackageToggle = (id) => {
    setOpenPackageId((current) => (current === id ? null : id));
  };

  const buildBookingMailto = (pkg) => {
    const subject = `Booking Inquiry — ${pkg.name}`;
    const bodyLines = [
      `Hi Dionlovelle,`,
      ``,
      `I'd like to book the ${pkg.name} (${pkg.priceRange}).`,
      ``,
      `Event type:`,
      `Event date:`,
      `Event location / venue:`,
      `Approximate guest count:`,
      `Any special song requests:`,
      ``,
      `Looking forward to hearing from you!`,
      ``,
      `— —`,
      `Reply to: ${BOOKING_EMAIL}`,
      `Or call: ${BOOKING_PHONE}`,
    ];
    const body = encodeURIComponent(bodyLines.join('\n'));
    return `mailto:${BOOKING_EMAIL}?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

  const services = [
    {
      id: 'session-vocals-premium',
      name: 'Session Vocals (Premium)',
      price: 600,
      icon: <Mic size={40} />,
      description: 'Professional vocal recording with revisions',
      details: ['Studio-quality recordings', 'Multiple takes included', 'Vocal arrangement assistance', 'Unlimited revisions', 'Commercial rights included'],
    },
    {
      id: 'session-writing',
      name: 'Session Writing',
      price: 800,
      icon: <Pen size={40} />,
      description: 'Expert songwriting and composition services',
      details: ['Lyrics & melody composition', 'Genre-specific expertise', 'Unlimited revisions', 'Co-writing collaboration'],
    },
    {
      id: 'special-appearances',
      name: 'Special Appearances',
      price: 1200,
      icon: <Star size={40} />,
      description: 'Personal appearance at your special event',
      details: ['Red carpet appearances', 'Video cameos', 'Brand collaborations', 'Social media features'],
    },
  ];

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setError('');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedService) {
      setError('Please select a service');
      return;
    }

    if (!formData.name || !formData.email || !formData.date || !formData.time) {
      setError('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const bookingData = {
        name: formData.name,
        email: formData.email,
        service: selectedService.name,
        date: formData.date,
        time: formData.time,
        price: selectedService.price,
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      
      if (response.data) {
        setSubmitted(true);
        
        // Send confirmation email to customer
        const emailBody = `Thank you for booking ${selectedService.name} with Dionlovelle!%0D%0A%0D%0ABooking Details:%0D%0AService: ${selectedService.name}%0D%0ADate: ${formData.date}%0D%0ATime: ${formData.time}%0D%0APrice: $${selectedService.price}%0D%0A%0D%0AI will be in touch shortly to confirm all details.%0D%0A%0D%0ABest,%0D%0ADionlovelle`;
        
        // This opens the user's email client with pre-filled content
        window.location.href = `mailto:${formData.email}?subject=Booking Confirmation - ${selectedService.name}&body=${emailBody}`;
      }
    } catch (err) {
      console.error('Booking error:', err);
      setError('There was an error processing your booking. Please try again or contact dionlovellebooking@gmail.com directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center animate-fade-in">
          <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={60} className="text-green-500" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Booking Confirmed!</h1>
          <p className="text-xl text-gray-300 mb-6">
            Thank you for booking with Dionlovelle
          </p>
          <p className="text-gray-400 mb-8 max-w-md mx-auto break-words">
            A confirmation email has been sent to <strong className="break-all">{formData.email}</strong>.
            You&apos;ll also receive an email at <strong className="break-all">dionlovellebooking@gmail.com</strong> with your booking details.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/" className="glow-button" data-testid="return-home-button">
              Return Home
            </Link>
            <a 
              href="mailto:dionlovellebooking@gmail.com"
              className="glow-button"
              data-testid="contact-email-button"
            >
              <Mail className="inline mr-2" size={20} />
              Contact Me
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <Nav />

      {/* Value Prop Banner */}
      <section className="pt-24 sm:pt-28 pb-2 px-4 sm:px-6" data-testid="booking-intro-banner">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card px-6 py-6 sm:px-10 sm:py-8 text-center border border-purple-500/30">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-3 leading-tight">
              Live Music for Weddings &amp; Private Events
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl mx-auto">
              From intimate acoustic performances to full-band experiences — professional, seamless, and tailored to your event.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="pt-6 sm:pt-10 pb-8 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center animate-fade-in">
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6 gradient-text px-2" data-testid="booking-title">Book Dionlovelle</h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto px-4">
            Trusted by event planners, venues, and hosts to deliver unforgettable live music.
          </p>
        </div>
      </section>

      {/* Live Performance Packages — Accordion */}
      <section className="py-8 sm:py-12 px-4 sm:px-6" data-testid="live-packages-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs sm:text-sm font-bold px-4 sm:px-6 py-2 rounded-full mb-3">
              LIVE PERFORMANCE PACKAGES
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold gradient-text mb-3">Choose Your Experience</h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto px-2">
              Tap a package to see what&apos;s included. Every performance is tailored to your event.
            </p>
          </div>

          <div className="space-y-4">
            {livePackages.map((pkg) => {
              const isOpen = openPackageId === pkg.id;
              const isPremium = pkg.id === 'premium';
              return (
                <div
                  key={pkg.id}
                  data-testid={`package-${pkg.id}`}
                  className={`glass-card overflow-hidden transition-all duration-500 ${
                    isOpen
                      ? 'border-2 border-purple-500 shadow-[0_0_60px_rgba(139,92,246,0.35)]'
                      : 'border border-white/10 hover:border-purple-500/40'
                  } ${isPremium && !isOpen ? 'ring-1 ring-yellow-400/40' : ''}`}
                >
                  {/* Header (always visible) */}
                  <button
                    type="button"
                    onClick={() => handlePackageToggle(pkg.id)}
                    className="w-full text-left px-5 sm:px-7 py-5 sm:py-6 flex items-center justify-between gap-4 group"
                    aria-expanded={isOpen}
                    data-testid={`package-${pkg.id}-toggle`}
                  >
                    <div className="flex items-center gap-4 sm:gap-5 min-w-0 flex-1">
                      <div
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${pkg.accent} text-black shadow-lg`}
                      >
                        {pkg.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center flex-wrap gap-2 mb-1">
                          <h3 className="text-lg sm:text-2xl font-bold break-words">{pkg.name}</h3>
                          {pkg.badge && (
                            <span className="inline-flex items-center gap-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-[10px] sm:text-xs font-extrabold px-2.5 py-1 rounded-full tracking-wide">
                              ⭐ {pkg.badge}
                            </span>
                          )}
                        </div>
                        <p className={`text-lg sm:text-xl font-bold bg-gradient-to-r ${pkg.accent} bg-clip-text text-transparent`}>
                          {pkg.priceRange}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border transition-all ${
                        isOpen
                          ? 'bg-purple-500 border-purple-500 rotate-180'
                          : 'bg-white/5 border-white/20 group-hover:border-purple-400'
                      }`}
                    >
                      <ChevronDown size={20} />
                    </div>
                  </button>

                  {/* Expanded content */}
                  <div
                    className={`grid transition-all duration-500 ease-in-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 sm:px-7 pb-6 sm:pb-7 pt-1">
                        <div className={`h-px w-full bg-gradient-to-r ${pkg.accent} opacity-40 mb-5`} />
                        <p className="text-sm sm:text-base text-gray-200 mb-4 italic">{pkg.tagline}</p>
                        <ul className="space-y-3 mb-6" data-testid={`package-${pkg.id}-features`}>
                          {pkg.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-gray-100">
                              <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br ${pkg.accent} flex items-center justify-center`}>
                                <Check size={12} className="text-black" strokeWidth={3} />
                              </div>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="bg-black/40 border border-purple-500/20 rounded-xl p-4 sm:p-5 mb-5">
                          <p className="text-xs uppercase tracking-widest text-purple-300 mb-2">Book This Package</p>
                          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 text-sm">
                            <a
                              href={`mailto:${BOOKING_EMAIL}`}
                              className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
                              data-testid={`package-${pkg.id}-email`}
                            >
                              <Mail size={16} className="flex-shrink-0" />
                              <span className="break-all">{BOOKING_EMAIL}</span>
                            </a>
                            <a
                              href={`tel:+1${BOOKING_PHONE.replace(/-/g, '')}`}
                              className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
                              data-testid={`package-${pkg.id}-phone`}
                            >
                              <Phone size={16} className="flex-shrink-0" />
                              <span>{BOOKING_PHONE}</span>
                            </a>
                          </div>
                        </div>

                        <a
                          href={buildBookingMailto(pkg)}
                          className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 font-bold text-black text-base sm:text-lg px-8 py-3.5 rounded-full bg-gradient-to-r ${pkg.accent} hover:brightness-110 transition-all shadow-lg hover:shadow-[0_0_40px_rgba(139,92,246,0.45)]`}
                          data-testid={`package-${pkg.id}-book-button`}
                        >
                          <Mail size={18} />
                          Book {pkg.name}
                        </a>
                        <p className="text-xs text-gray-400 mt-3">
                          Opens your email with details pre-filled. I usually reply within 24 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-center text-sm text-gray-400 mt-6">
            Need something custom?{' '}
            <a
              href={`mailto:${BOOKING_EMAIL}?subject=Custom%20Booking%20Inquiry`}
              className="text-purple-400 hover:text-purple-300 underline underline-offset-2"
              data-testid="custom-inquiry-link"
            >
              Send me a custom request
            </a>
            .
          </p>
        </div>
      </section>

      {/* Performance Configurations (Services Section) */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent" data-testid="configurations-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 text-purple-200 text-xs sm:text-sm font-semibold px-4 py-2 rounded-full mb-4">
              <Mic size={14} />
              SERVICES
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold gradient-text mb-3">Performance Configurations</h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
              Choose the sound that fits your event. Contact for pricing — every quote is custom.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {/* Solo Acoustic */}
            <div className="glass-card p-6 sm:p-7 border border-white/10 hover:border-purple-500/40 transition-all group" data-testid="config-solo-acoustic">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-400 text-black flex items-center justify-center mb-5 shadow-lg">
                <Mic size={24} />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Solo Acoustic</h3>
              <p className="text-sm text-gray-400 mb-5">Perfect for ceremonies, cocktail hours, and intimate settings</p>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2 text-sm sm:text-base text-gray-100"><Check size={16} className="mt-0.5 text-sky-400 flex-shrink-0" />Vocals + guitar</li>
                <li className="flex items-start gap-2 text-sm sm:text-base text-gray-100"><Check size={16} className="mt-0.5 text-sky-400 flex-shrink-0" />Elegant, warm atmosphere</li>
                <li className="flex items-start gap-2 text-sm sm:text-base text-gray-100"><Check size={16} className="mt-0.5 text-sky-400 flex-shrink-0" />Minimal setup, quick transitions</li>
              </ul>
            </div>

            {/* Duo / Trio */}
            <div className="glass-card p-6 sm:p-7 border border-white/10 hover:border-purple-500/40 transition-all group" data-testid="config-duo-trio">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-black flex items-center justify-center mb-5 shadow-lg">
                <Users size={24} />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Duo / Trio</h3>
              <p className="text-sm text-gray-400 mb-5">A fuller sound while keeping a refined feel</p>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2 text-sm sm:text-base text-gray-100"><Check size={16} className="mt-0.5 text-orange-400 flex-shrink-0" />Guitar + keys / sax / percussion</li>
                <li className="flex items-start gap-2 text-sm sm:text-base text-gray-100"><Check size={16} className="mt-0.5 text-orange-400 flex-shrink-0" />Ideal for cocktail hour + dinner</li>
              </ul>
            </div>

            {/* Full Band */}
            <div className="glass-card p-6 sm:p-7 border border-white/10 hover:border-purple-500/40 transition-all group" data-testid="config-full-band">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-400 to-purple-500 text-black flex items-center justify-center mb-5 shadow-lg">
                <Music size={24} />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Full Band</h3>
              <p className="text-sm text-gray-400 mb-5">High-energy live music for receptions and large events</p>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2 text-sm sm:text-base text-gray-100"><Check size={16} className="mt-0.5 text-pink-400 flex-shrink-0" />Customizable band size</li>
                <li className="flex items-start gap-2 text-sm sm:text-base text-gray-100"><Check size={16} className="mt-0.5 text-pink-400 flex-shrink-0" />Dance floor-ready</li>
                <li className="flex items-start gap-2 text-sm sm:text-base text-gray-100"><Check size={16} className="mt-0.5 text-pink-400 flex-shrink-0" />Professional musicians</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-12 sm:py-16 px-4 sm:px-6" data-testid="included-section">
        <div className="max-w-5xl mx-auto">
          <div className="glass-card p-6 sm:p-10 border border-purple-500/20">
            <div className="flex flex-col md:flex-row md:items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                <Volume2 size={28} />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-1">What&apos;s Included</h2>
                <p className="text-gray-400 text-sm sm:text-base">Every booking includes a professional, stress-free setup.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[
                'Full sound system (speaker + mic)',
                'Setup and breakdown',
                'Coordination with planner or venue',
                'Custom song requests (when applicable)',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 hover:bg-purple-500/5 transition-all" data-testid={`included-item-${i}`}>
                  <div className="w-6 h-6 mt-0.5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <Check size={14} className="text-white" strokeWidth={3} />
                  </div>
                  <span className="text-sm sm:text-base text-gray-100">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Clients Choose Us */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent" data-testid="why-choose-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 text-purple-200 text-xs sm:text-sm font-semibold px-4 py-2 rounded-full mb-4">
              <Award size={14} />
              WHY CLIENTS CHOOSE US
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold gradient-text">Booking made easy</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              { icon: <Clock size={22} />, text: 'Reliable, on-time professionals' },
              { icon: <Mail size={22} />, text: 'Clear communication from booking to event day' },
              { icon: <Sparkles size={22} />, text: 'Flexible setups for any type of event' },
              { icon: <Award size={22} />, text: 'Trusted by planners and venues' },
            ].map((r, i) => (
              <div
                key={i}
                className="glass-card p-5 sm:p-6 flex items-start gap-4 border border-white/10 hover:border-purple-500/40 transition-all"
                data-testid={`reason-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-purple-500/15 border border-purple-500/30 text-purple-300 flex items-center justify-center flex-shrink-0">
                  {r.icon}
                </div>
                <p className="text-base sm:text-lg text-gray-100 font-medium leading-snug">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tell us about your event — Quick inquiry form */}
      <section className="py-12 sm:py-16 px-4 sm:px-6" data-testid="event-inquiry-section">
        <div className="max-w-3xl mx-auto">
          <div className="glass-card p-6 sm:p-10 border-2 border-purple-500/40 shadow-[0_0_60px_rgba(139,92,246,0.2)]">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs sm:text-sm font-bold px-4 py-2 rounded-full mb-4">
                <Send size={14} />
                CONTACT
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold gradient-text mb-3">Tell us about your event</h2>
              <p className="text-gray-300 text-sm sm:text-base">We&apos;ll take care of the music.</p>
            </div>

            <EventInquiryForm bookingEmail={BOOKING_EMAIL} bookingPhone={BOOKING_PHONE} />

            <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center text-sm">
              <a
                href={`mailto:${BOOKING_EMAIL}`}
                className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
                data-testid="inquiry-email-link"
              >
                <Mail size={16} />
                <span className="break-all">{BOOKING_EMAIL}</span>
              </a>
              <a
                href={`tel:+1${BOOKING_PHONE.replace(/-/g, '')}`}
                className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
                data-testid="inquiry-phone-link"
              >
                <Phone size={16} />
                <span>{BOOKING_PHONE}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services Grid */}
      <section className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3 text-center gradient-text">Studio & Appearance Services</h2>
          <p className="text-center text-gray-400 mb-6 sm:mb-8 text-sm sm:text-base">Session vocals, songwriting, and special appearances</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {services.map((service) => (
              <div
                key={service.id}
                onClick={() => handleServiceSelect(service)}
                className={`glass-card p-4 sm:p-6 md:p-8 cursor-pointer transition-all relative ${
                  selectedService?.id === service.id
                    ? 'border-2 border-purple-500 bg-purple-500/10'
                    : 'hover:border-purple-500/50'
                }`}
                data-testid={`service-${service.id}`}
              >
                {service.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold px-4 py-1 rounded-full shadow-lg">
                      {service.badge}
                    </span>
                  </div>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div className="text-purple-400">{service.icon}</div>
                  {selectedService?.id === service.id && (
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                      <Check size={20} />
                    </div>
                  )}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 break-words">{service.name}</h3>
                <p className="text-3xl sm:text-4xl font-bold gradient-text mb-4">${service.price}</p>
                <p className="text-sm sm:text-base text-gray-400 mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.details.map((detail, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-gray-300">
                      <Check size={16} className="text-purple-400" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Booking Form */}
          {selectedService && (
            <div className="max-w-2xl mx-auto glass-card p-8 animate-fade-in" data-testid="booking-form">
              <h2 className="text-3xl font-bold mb-6 gradient-text text-center">Complete Your Booking</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                    <User size={18} />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="Your full name"
                    required
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                    <Mail size={18} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="your@email.com"
                    required
                    data-testid="input-email"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                      <Calendar size={18} />
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500 transition-colors"
                      required
                      data-testid="input-date"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                      <Clock size={18} />
                      Preferred Time
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500 transition-colors"
                      required
                      data-testid="input-time"
                    />
                  </div>
                </div>

                {/* Selected Service Summary */}
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-6">
                  <h3 className="font-bold text-lg mb-2">Booking Summary</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex justify-between">
                      <span className="text-gray-400">Service:</span>
                      <span className="font-semibold">{selectedService.name}</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-gray-400">Price:</span>
                      <span className="font-semibold text-2xl gradient-text">${selectedService.price}</span>
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm" data-testid="error-message">
                    {error}
                  </div>
                )}

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm text-yellow-200">
                  <div className="flex items-start gap-2">
                    <CreditCard size={18} className="mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="break-words">
                        Payment will be processed securely via Stripe. After submitting this form, you will receive 
                        booking confirmation details via email at{' '}
                        <strong className="break-all">dionlovellebooking@gmail.com</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full glow-button text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-booking-button"
                >
                  {isSubmitting ? 'Processing...' : 'Submit Booking Request'}
                </button>

                <p className="text-center text-sm text-gray-400">
                  By submitting, you agree to be contacted at the provided email address
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center glass-card p-8">
          <h3 className="text-2xl font-bold mb-4">Have Questions?</h3>
          <p className="text-gray-400 mb-6">
            Feel free to reach out for custom bookings, inquiries, or any questions
          </p>
          <a 
            href="mailto:dionlovellebooking@gmail.com"
            className="glow-button inline-flex items-center gap-2"
            data-testid="footer-email-button"
          >
            <Mail size={20} className="flex-shrink-0" />
            <span className="break-all">dionlovellebooking@gmail.com</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">© 2025 Dionlovelle. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BookingPage;

// ——— Event inquiry form ———
// Simple client-side form that composes a mailto: link so planners/clients can
// email Dionlovelle directly with all event details pre-filled. No backend needed.
const EventInquiryForm = ({ bookingEmail, bookingPhone }) => {
  const [inquiry, setInquiry] = useState({
    name: '',
    email: '',
    eventDate: '',
    location: '',
    eventType: 'Wedding',
    musicStyle: '',
    notes: '',
  });

  const handleChange = (e) => {
    setInquiry({ ...inquiry, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `Event Inquiry — ${inquiry.eventType}${inquiry.eventDate ? ` (${inquiry.eventDate})` : ''}`;
    const lines = [
      `Hi Dionlovelle,`,
      ``,
      `I'd like to inquire about live music for my event.`,
      ``,
      `Name: ${inquiry.name}`,
      `Email: ${inquiry.email}`,
      `Event date: ${inquiry.eventDate}`,
      `Location: ${inquiry.location}`,
      `Type of event: ${inquiry.eventType}`,
      `Preferred music style: ${inquiry.musicStyle}`,
      ``,
      `Additional notes:`,
      inquiry.notes || '(none)',
      ``,
      `Please send pricing and availability. Thanks!`,
      ``,
      `— — —`,
      `Reply to: ${bookingEmail}`,
      `Or call: ${bookingPhone}`,
    ];
    const body = encodeURIComponent(lines.join('\n'));
    window.location.href = `mailto:${bookingEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

  const inputCls =
    'w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-colors';

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="event-inquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Your name</label>
          <input
            type="text"
            name="name"
            value={inquiry.name}
            onChange={handleChange}
            required
            placeholder="Jane Doe"
            className={inputCls}
            data-testid="inquiry-name"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Email</label>
          <input
            type="email"
            name="email"
            value={inquiry.email}
            onChange={handleChange}
            required
            placeholder="you@email.com"
            className={inputCls}
            data-testid="inquiry-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Event date</label>
          <input
            type="date"
            name="eventDate"
            value={inquiry.eventDate}
            onChange={handleChange}
            required
            className={inputCls}
            data-testid="inquiry-date"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Location</label>
          <input
            type="text"
            name="location"
            value={inquiry.location}
            onChange={handleChange}
            required
            placeholder="City / venue"
            className={inputCls}
            data-testid="inquiry-location"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Type of event</label>
          <select
            name="eventType"
            value={inquiry.eventType}
            onChange={handleChange}
            className={inputCls}
            data-testid="inquiry-event-type"
          >
            <option className="bg-black" value="Wedding">Wedding</option>
            <option className="bg-black" value="Private Dinner">Private Dinner</option>
            <option className="bg-black" value="Cocktail Hour">Cocktail Hour</option>
            <option className="bg-black" value="Hotel / Lounge">Hotel / Lounge</option>
            <option className="bg-black" value="Corporate Event">Corporate Event</option>
            <option className="bg-black" value="Other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Preferred music style</label>
          <input
            type="text"
            name="musicStyle"
            value={inquiry.musicStyle}
            onChange={handleChange}
            placeholder="R&B, acoustic, soul, romantic..."
            className={inputCls}
            data-testid="inquiry-music-style"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Anything else?</label>
        <textarea
          name="notes"
          value={inquiry.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Guest count, timing, must-play songs, logistics…"
          className={inputCls}
          data-testid="inquiry-notes"
        />
      </div>

      <button
        type="submit"
        className="w-full inline-flex items-center justify-center gap-2 font-bold text-black text-base sm:text-lg px-8 py-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:brightness-110 transition-all shadow-lg hover:shadow-[0_0_40px_rgba(139,92,246,0.45)]"
        data-testid="inquiry-submit-button"
      >
        <Send size={18} />
        Send Inquiry
      </button>
      <p className="text-center text-xs text-gray-400">
        Opens your email client with all details pre-filled. Usually a reply within 24 hours.
      </p>
    </form>
  );
};
